/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect } from 'react'
import useBreadcrumb from '../../../NavBar/useBreadcrumb'
import { useTranslation } from 'react-i18next'
import { DraggableList } from './DraggableList'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAisles, setPickingPath } from '../../../../services/Provider/Provider.service'
import useUser from '../../../Shared/UserProvider/useUser'
import { Button, Header, Loader } from 'semantic-ui-react'
import { notify } from 'react-notify-toast'
import { getAislesNotInPickingPath, usePickingPath } from './usePickingPath'
import { useNavigate } from 'react-router-dom-v5-compat'
import { FeatureInfo } from '../../../Shared/FeatureInfo/FeatureInfo'

const flattenPickingPath = path => {
    const flattenedPath = []
    const process = aisle => {
        flattenedPath.push(aisle.id)
        aisle.aisles?.forEach(process)
    }
    path.forEach(process)

    return flattenedPath
}

const PickingPath = () => {
    const [t] = useTranslation()
    const navigate = useNavigate()

    const {setPaths} = useBreadcrumb()
    const {user} = useUser()
    const queryClient = useQueryClient()

    const {pickingPath, isPickingPathLoading} = usePickingPath()

    const {data: aisles, isLoading: isAislesLoading} = useQuery({
        queryFn: () => getAisles(user.provider.id),
        queryKey: ['providers', user.provider.id, 'aisles'],
    })

    const setPickingPathMutation = useMutation({
        mutationFn: setPickingPath,
        onSuccess: () => {
            notify.show(t('global.registerSuccess'), 'success')
            queryClient.invalidateQueries({
                queryKey: ['providers', user.provider.id, 'picking-path']
            })
        }
    })

    const navigateBack = () => {
        navigate('/settings/merchant')
    }

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.settings'),
            link: true,
            path: '/settings',
        }, {
            text: t('picking_path.picking_path'),
            link: false,
        }])
    }, [])

    const isLoading = isPickingPathLoading || isAislesLoading

    if (isLoading) {
        return <Loader active inline="centered"/>
    }

    const withdrawnAisles = getAislesNotInPickingPath(aisles, pickingPath?.path || [])

    return (
      <>
          <div css={css`
              display: inline-flex;
              align-items: center;
          `}
          >
              <Button
                basic
                size="mini"
                icon="arrow left"
                content={t('global.back')}
                onClick={navigateBack}
              />
              <Header
                css={css`
                    display: inline;
                    &.ui.header {
                        margin: 0 .5rem;
                    }
                `}
              >
                  {t('picking_path.picking_path')}
              </Header>
          </div>


          <FeatureInfo featureName="picking-path"
                       css={css`
                           &.ui.message {
                               margin-bottom: 5rem;
                           }

                           &.blue.info.circle.icon {
                               margin-bottom: 6rem;
                               width: 16px;
                               height: 16px;
                               vertical-align: text-top;
                           }
                       `}>
              <FeatureInfo.Text>{t('picking_path.description')}</FeatureInfo.Text>
              <FeatureInfo.List>
                  <FeatureInfo.ListItem>{t('picking_path.how_to_use')}</FeatureInfo.ListItem>
                  <FeatureInfo.ListItem>{t('picking_path.how_to_use_step_1')}</FeatureInfo.ListItem>
                  <FeatureInfo.ListItem>{t('picking_path.how_to_use_step_2')}</FeatureInfo.ListItem>
              </FeatureInfo.List>
          </FeatureInfo>

          <DraggableList
            pickingPath={pickingPath?.path || []}
            withdrawnAisles={withdrawnAisles}
            onChange={newPath => {
                setPickingPathMutation.mutate({
                    providerId: user.provider.id,
                    path: flattenPickingPath(newPath)
                })
            }}
          />
      </>
    )
}

export { PickingPath }
